import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useApi from '../../hooks/use-api';
import { buildAuthenticatedFileUrl } from '../../utils/utils';
import { OutlinedCheck, OutlinedError } from '../../icons';

type FileDetails = {
  id: number;
  name: string;
  url: string;
  expirationDate: string;
  notes: string;
  approved: boolean;
};

const getFileDisplayName = (value: string) => {
  const rawValue = (value || '').trim();
  if (!rawValue) return '';

  try {
    if (/^https?:\/\//i.test(rawValue)) {
      const parsed = new URL(rawValue);
      const segments = parsed.pathname.split('/').filter(Boolean);
      return segments[segments.length - 1] || rawValue;
    }
  } catch {
    // Fall back to path split below.
  }

  const normalized = rawValue.replace(/\\/g, '/');
  const segments = normalized.split('/').filter(Boolean);
  return segments[segments.length - 1] || rawValue;
}

const normalizeFile = (data: any): FileDetails | null => {
  const file = data?.file ?? data;
  if (!file || typeof file.id === 'undefined') {
    return null;
  }

  return {
    id: file.id,
    name: getFileDisplayName(file.title || file.filename),
    url: file.filename,
    expirationDate: file.expiration_date || '',
    notes: file.notes || '',
    approved: !!file.approved,
  };
}

export default function FileEditPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [file, setFile] = useState<FileDetails | null>(null);
  const [values, setValues] = useState<{ expirationDate: string; notes: string }>({ expirationDate: '', notes: '' });
  const [saved, setSaved] = useState(false);

  const getFile = useApi({
    path: `/file_token/${token}`,
    callback: (data) => {
      const normalized = normalizeFile(data);
      setFile(normalized);
      if (normalized) {
        setValues({ expirationDate: normalized.expirationDate, notes: normalized.notes });
      }
    },
  });

  const saveFile = useApi({
    path: `/file_token/${token}`,
    defaultSuccessMessage: 'File details updated.',
    callback: () => setSaved(true),
  });

  useEffect(() => {
    if (token) {
      getFile.get();
    }
  }, [token]);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSaved(false);
    saveFile.put({
      expiration_date: values.expirationDate,
      notes: values.notes,
    });
  }

  return (
    <div
      className="relative flex min-h-screen w-full flex-col items-center justify-center bg-cover bg-center"
      style={{
        background: `linear-gradient(180deg, #086CE1 0%, rgba(8, 108, 225, 0) 98%), linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 98%), url('/images/account-background.jpg') lightgray -2.651px -381.993px/100.239% 142.326% no-repeat`,
      }}
    >
      <div className="relative z-10 flex w-full flex-col items-center">
        <h1 className="mx-auto mb-12 text-white max-w-lg text-center text-3xl font-extrabold text-primary drop-shadow-lg md:text-4xl">
          Edit File Details
        </h1>
        <div className="w-full max-w-md px-4">
          {!token && (
            <div role="alert" className="mb-4 flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
              <OutlinedError width={20} height={20} hardcodedColor="#DC2626" className="mt-0.5 shrink-0" />
              <span>This file link is invalid or has expired.</span>
            </div>
          )}
          {token && getFile.errors && (
            <div role="alert" className="mb-4 flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
              <OutlinedError width={20} height={20} hardcodedColor="#DC2626" className="mt-0.5 shrink-0" />
              <span>{getFile.errors}</span>
            </div>
          )}
          {saveFile.errors && (
            <div role="alert" className="mb-4 flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
              <OutlinedError width={20} height={20} hardcodedColor="#DC2626" className="mt-0.5 shrink-0" />
              <span>{saveFile.errors}</span>
            </div>
          )}
          {saved && (
            <div role="status" className="mb-4 flex items-start gap-2 rounded-lg border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-700">
              <OutlinedCheck width={20} height={20} hardcodedColor="#15803D" className="mt-0.5 shrink-0" />
              <span>File details updated.</span>
            </div>
          )}

          {getFile.loading && !file && (
            <div className="flex justify-center py-8">
              <span className="text-sm font-medium text-white">Loading file details…</span>
            </div>
          )}

          {file && (
            <form className="w-full rounded-lg bg-white p-6 shadow-md" onSubmit={onSubmit}>
              <div className="mb-4">
                <span className="block text-xs font-medium text-gray-500">File</span>
                <a
                  href={buildAuthenticatedFileUrl(file.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base font-semibold text-blue-700 underline"
                >
                  {file.name}
                </a>
              </div>
              <label className="mb-4 block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Expiration date</span>
                <input
                  type="date"
                  className="w-full rounded border border-gray-300 px-2 py-2 text-sm"
                  value={values.expirationDate}
                  onChange={(e) => setValues((prev) => ({ ...prev, expirationDate: e.target.value }))}
                />
              </label>
              <label className="mb-4 block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Notes</span>
                <input
                  type="text"
                  className="w-full rounded border border-gray-300 px-2 py-2 text-sm"
                  value={values.notes}
                  onChange={(e) => setValues((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </label>
              <button
                type="submit"
                className="mt-2 w-full rounded bg-blue-600 py-3 text-lg font-bold text-white shadow-md transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={saveFile.loading}
              >
                Save
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
